import React from 'react';
import { Control, Controller } from 'react-hook-form';

import { useTranslation } from '@mint-lib/i18n';

import {
  Box,
  Button,
  CircularProgress,
  Input,
  PercentField,
  SwitchWithLabel,
  Typography,
} from '@myn/mui';

import type { TechCostColumn } from '../../../../types/buyingPlan.js';

type Props = {
  control: Control<TechCostColumn, any>;
  isDisabled: boolean;
  isSubmitting: boolean;
  handleCancel: () => void;
  isAutoCalculationEnabled: boolean;
};

const TechCostFormContent: React.FC<Props> = ({
  control,
  isDisabled,
  isSubmitting,
  handleCancel,
  isAutoCalculationEnabled,
}) => {
  const { t } = useTranslation('@myn/planning');

  if (isSubmitting) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', minHeight: '216px' }}
      >
        <CircularProgress size={30} />
      </Box>
    );
  }

  return (
    <>
      <Controller
        control={control}
        name="name"
        render={({ field, fieldState }) => (
          <Input
            label={t('Tech cost name')}
            placeholder={t('Column name')}
            value={field.value}
            onChange={field.onChange}
            error={Boolean(fieldState.error)}
            helperText={fieldState.error?.message}
            fullWidth
          />
        )}
      />

      <Controller
        control={control}
        name="isAutoCalculationEnabled"
        render={({ field }) => (
          <SwitchWithLabel
            label={t('Automated calculations')}
            labelPlacement="end"
            size="small"
            checked={field.value}
            onChange={field.onChange}
          />
        )}
      />

      <Controller
        control={control}
        name="techCostPercent"
        render={({ field, fieldState, formState }) => (
          <PercentField
            maxDecimals={2}
            value={field.value}
            onChange={field.onChange}
            disabled={!isAutoCalculationEnabled}
            placeholder={t('Insert value')}
            error={isAutoCalculationEnabled && Boolean(fieldState.error)}
            helperText={
              isAutoCalculationEnabled ? fieldState.error?.message : ''
            }
            InputProps={{
              endAdornment: (
                <Typography
                  variant="label01"
                  color={({ palette }) => palette.text.secondary}
                  sx={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'flex-end',
                  }}
                >
                  {t('of Net Media Budget')}
                </Typography>
              ),
            }}
            restFormatProps={{
              min: 0.01,
              max: 100,
            }}
            fullWidth
          />
        )}
      />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: (theme) => theme.spacing(1),
        }}
      >
        <Button variant="text" onClick={handleCancel}>
          {t('Cancel')}
        </Button>
        <Button disabled={isDisabled} type="submit">
          {t('Save')}
        </Button>
      </Box>
    </>
  );
};

export default TechCostFormContent;
